import { isGiantId, type GiantId } from '../ids/ids';
import { GIANT_LEVEL_ID_SUFFIX } from '../catalog/flightPhysics';
import type { LevelId } from '../level/level';
import type { MissionRef } from '../catalog/mission';
import type { DeliveryRatingBreakdown } from '../rating/deliveryRating';

export type DeliveryUrgency = 'regular' | 'important' | 'urgent';

export type DeliveryPoolEntryStatus = 'active' | 'failed' | 'completed';

export type DeliveryUpdateReason =
  | 'new'
  | 'refresh'
  | 'replay_improved'
  | 'replay_not_improved';

export type DeliveryHistoryOutcome = 'completed' | 'failed';

export type DeliveryHistoryEntry = {
  deliveryId: string;
  giantId: GiantId;
  orderIndex: number;
  outcome: DeliveryHistoryOutcome;
  bestRating: number;
  lastRating: DeliveryRatingBreakdown | null;
  lastSparksCollected: number;
  lastSparksTotal: number;
  updatedAt: number;
};

export type DeliveryPoolEntry = {
  deliveryId: string;
  levelId: LevelId;
  giantId: GiantId;
  orderIndex: number;
  createdAt: number;
  updatedAt: number;
  attemptsLeft: number;
  bestRating: number;
  status: DeliveryPoolEntryStatus;
  urgency: DeliveryUrgency;
  /** День ожидания заказа (1 — в день появления, +1 за каждый календарный день без взятия). */
  waitingDay: number;
  /** День ожидания на момент первой попытки (для награды билетами). */
  takenOnWaitingDay?: number | null;
  passRewardClaimed?: boolean;
  /**
   * Временная запись для повтора заказа из истории вне дневной пачки.
   * Не учитывается в размере пачки и не показывается на карте.
   */
  fromHistoryReplay?: boolean;
  /** @deprecated Используйте {@link urgency}. */
  urgent?: boolean;
  urgentExpiresAt?: number | null;
  urgentRewardClaimed?: boolean;
};

export type PlayerStickerProgress = {
  id: string;
  unlockedAt: number;
};

export type PlayerEconomyState = {
  /** Средний рейтинг доставок (ratingSum / ratedDeliveriesCount). */
  totalRating: number;
  /** Сумма лучших оценок по всем доставкам с хотя бы одним успешным прохождением. */
  ratingSum: number;
  /** Число доставок, по которым есть лучшая оценка. */
  ratedDeliveriesCount: number;
  perfectDeliveriesCount: number;
  passesBalance: number;
  streakDays: number;
  lastLoginDate: string | null;
  /** ISO-дата (YYYY-MM-DD), когда игрок последний раз забрал награду за ежедневный вход. */
  lastDailyRewardClaimedDate?: string | null;
  /** Игрок завершил челлендж «7 дней подряд» и больше не получает награду за вход. */
  dailyLoginStreakCompleted?: boolean;
  urgentCompletedCount?: number;
  stickers?: PlayerStickerProgress[];
  /** Id вопросов квиза, на которые игрок уже ответил (верно или нет). */
  answeredQuizQuestionIds?: string[];
  /** ISO-дата МСК (YYYY-MM-DD) последнего ответа в квизе. */
  quizDailyDate?: string | null;
  /** Сколько вопросов квиза отвечено в день {@link quizDailyDate}. */
  quizDailyAnsweredCount?: number;
  /** Награда за включение уведомлений от игры уже выдана. */
  notificationsRewardClaimed?: boolean;
  /** Награда за подписку на сообщество VK уже выдана. */
  vkSubscribeRewardClaimed?: boolean;
  deliveryPool: DeliveryPoolEntry[];
  /** ISO-дата (YYYY-MM-DD) выдачи текущей дневной пачки. */
  deliveryPoolDate?: string | null;
  /** Все заказы пачки закрыты (успех или провал) — ждём следующий день. */
  deliveryPoolExhausted?: boolean;
  /** ISO-дата, когда игрок закрыл окно «на сегодня всё». */
  deliveryPackCompleteAckDate?: string | null;
  /** Все попытки доставок игрока (для экрана «Мои доставки»). */
  deliveryHistory?: DeliveryHistoryEntry[];
  /**
   * Гиганты, чей участок карты уже открывался (был заказ в пуле).
   * Раскраска sticky — не зависит от текущего active-статуса.
   */
  openedMapGiantIds?: GiantId[];
  lastPoolRotationAt: number;
};

export function isUrgentPoolEntry(entry: DeliveryPoolEntry): boolean {
  return entry.urgency === 'urgent';
}

export function isImportantPoolEntry(entry: DeliveryPoolEntry): boolean {
  return entry.urgency === 'important';
}

export function deliveryIdFromMission(mission: MissionRef): string {
  return `${mission.giantId}:${mission.orderIndex}`;
}

function giantIdFromDeliveryIdPart(part: string): GiantId | null {
  if (isGiantId(part)) {
    return part;
  }
  if (part.endsWith(GIANT_LEVEL_ID_SUFFIX)) {
    const stripped = part.slice(0, -GIANT_LEVEL_ID_SUFFIX.length);
    if (isGiantId(stripped)) {
      return stripped;
    }
  }
  return null;
}

export function missionFromDeliveryId(deliveryId: string): MissionRef | null {
  const sep = deliveryId.indexOf(':');
  if (sep <= 0) {
    return null;
  }
  const giantId = giantIdFromDeliveryIdPart(deliveryId.slice(0, sep));
  const orderIndex = Number.parseInt(deliveryId.slice(sep + 1), 10);
  if (giantId == null || !Number.isInteger(orderIndex) || orderIndex < 0) {
    return null;
  }
  return { giantId, orderIndex };
}
